import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { IconSparkles, IconCheck, IconX, IconAlertTriangle, IconEdit } from '@tabler/icons-react';

/**
 * AnalyzeModal — base genérico para modais de análise/melhoria por IA.
 *
 * Props:
 *  - isOpen: bool
 *  - onClose: () => void
 *  - onApply: (newValue: string) => void
 *  - title: ReactNode — conteúdo do Modal.Title
 *  - currentValue: string — valor atual (painel esquerdo do diff)
 *  - currentValueLabel: string — label do painel esquerdo (default: "Atual")
 *  - improvedValueLabel: string — label do painel direito (default: "Sugerida")
 *  - analysis: object|null — resultado do backend
 *  - improvedField: string — campo de analysis com o valor sugerido (default: "improved_description")
 *  - showOverlapWarnings: bool — exibe seção de sobreposição (default: false)
 *  - showExamples: bool — exibe seção de exemplos matching/non-matching (default: false)
 *  - loading: bool
 *  - error: string|null
 *  - onRetry: () => void
 */
export const AnalyzeModal = ({
  isOpen,
  onClose,
  onApply,
  title,
  currentValue,
  currentValueLabel = 'Atual',
  improvedValueLabel = 'Sugerida',
  analysis,
  improvedField = 'improved_description',
  showOverlapWarnings = false,
  showExamples = false,
  loading,
  error,
  onRetry,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editedValue, setEditedValue] = useState('');

  useEffect(() => {
    if (analysis?.[improvedField]) {
      setEditedValue(analysis[improvedField]);
    }
    setEditMode(false);
  }, [analysis, improvedField]);

  const handleApply = () => {
    const finalValue = editMode
      ? editedValue.trim()
      : (analysis?.[improvedField] || '').trim();
    if (!finalValue) return;
    onApply(finalValue);
  };

  const renderScoreBadge = (score, label) => {
    let variant = 'secondary';
    let emoji = '⚪';
    if (typeof score === 'number') {
      if (score >= 85) { variant = 'success'; emoji = '🟢'; }
      else if (score >= 60) { variant = 'warning'; emoji = '🟡'; }
      else { variant = 'danger'; emoji = '🔴'; }
    }
    return (
      <span>
        <small className="text-muted me-1">{label}</small>
        <Badge bg={variant}>{emoji} {score ?? '?'}/100</Badge>
      </span>
    );
  };

  const renderSeverityIcon = (severity) => {
    const colors = { high: '#dc3545', medium: '#fd7e14', low: '#6c757d' };
    return <IconAlertTriangle size={14} color={colors[severity] || '#6c757d'} />;
  };

  const improvedValue = analysis?.[improvedField] || '';

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconSparkles size={18} className="me-2 text-primary" />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted">Analisando com a IA...</div>
          </div>
        )}

        {error && !loading && (
          <Alert variant="danger" className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Erro:</strong> {error}
            </div>
            {onRetry && (
              <Button size="sm" variant="outline-danger" onClick={onRetry}>
                Tentar de novo
              </Button>
            )}
          </Alert>
        )}

        {!loading && !error && analysis && (
          <>
            {/* Scores */}
            <div className="d-flex gap-3 align-items-center mb-3 pb-3 border-bottom">
              {renderScoreBadge(analysis.current_score, 'Atual:')}
              <span className="text-muted">→</span>
              {renderScoreBadge(analysis.improvement_score, 'Sugerida:')}
              {analysis._meta?.cached && (
                <Badge bg="light" text="dark" className="ms-auto">
                  <small>cache</small>
                </Badge>
              )}
            </div>

            {/* Issues */}
            {Array.isArray(analysis.issues) && analysis.issues.length > 0 && (
              <div className="mb-3">
                <h6 className="text-muted mb-2">Problemas detectados</h6>
                <ul className="list-unstyled mb-0">
                  {analysis.issues.map((issue, i) => (
                    <li key={i} className="mb-1 d-flex align-items-start gap-2">
                      <span className="mt-1">{renderSeverityIcon(issue.severity)}</span>
                      <span style={{ fontSize: 13 }}>{issue.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Overlap warnings — apenas para intent_router */}
            {showOverlapWarnings &&
              Array.isArray(analysis.overlap_warnings) &&
              analysis.overlap_warnings.length > 0 && (
                <div className="mb-3">
                  <h6 className="text-muted mb-2">Sobreposição com outras rotas</h6>
                  {analysis.overlap_warnings.map((w, i) => (
                    <Alert key={i} variant="warning" className="py-2 mb-1" style={{ fontSize: 13 }}>
                      <strong>↔ {w.with_intent}:</strong> {w.reason}
                    </Alert>
                  ))}
                </div>
              )}

            {/* Diff atual vs sugerida */}
            <div className="mb-3">
              <h6 className="text-muted mb-2">Comparação</h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <Form.Label style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                    {currentValueLabel}
                  </Form.Label>
                  <div
                    style={{
                      fontSize: 13,
                      padding: 10,
                      backgroundColor: '#fff5f5',
                      border: '1px solid #fecaca',
                      borderRadius: 6,
                      minHeight: 100,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {currentValue || <em className="text-muted">(vazia)</em>}
                  </div>
                </div>
                <div className="col-md-6">
                  <Form.Label style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                    {improvedValueLabel} {editMode && <Badge bg="info" className="ms-1">editando</Badge>}
                  </Form.Label>
                  {editMode ? (
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      style={{ fontSize: 13 }}
                    />
                  ) : (
                    <div
                      style={{
                        fontSize: 13,
                        padding: 10,
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 6,
                        minHeight: 100,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {improvedValue || <em className="text-muted">(sem sugestão)</em>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Examples — apenas para intent_router */}
            {showExamples && (
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <h6 className="text-success mb-2" style={{ fontSize: 13 }}>
                    <IconCheck size={14} /> Vai capturar
                  </h6>
                  <ul className="list-unstyled" style={{ fontSize: 12 }}>
                    {(analysis.matching_examples || []).map((ex, i) => (
                      <li key={i} className="mb-1">
                        <code className="text-success">"{ex}"</code>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6 className="text-danger mb-2" style={{ fontSize: 13 }}>
                    <IconX size={14} /> NÃO vai capturar (vai pra default)
                  </h6>
                  <ul className="list-unstyled" style={{ fontSize: 12 }}>
                    {(analysis.non_matching_examples || []).map((ex, i) => (
                      <li key={i} className="mb-1">
                        <code className="text-danger">"{ex}"</code>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Reasoning */}
            {analysis.reasoning && (
              <div className="mb-1 p-2 rounded" style={{ backgroundColor: '#f8fafc', fontSize: 12 }}>
                <strong className="text-muted">Por quê:</strong> {analysis.reasoning}
              </div>
            )}

            {/* Meta */}
            {analysis._meta && (
              <div className="text-muted text-end" style={{ fontSize: 11 }}>
                {analysis._meta.provider} · {analysis._meta.model}
                {analysis._meta.prompt_version ? ` · ${analysis._meta.prompt_version}` : ''}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={onClose}>
          Cancelar
        </Button>
        {analysis && !loading && !error && (
          <>
            {!editMode && (
              <Button variant="outline-secondary" onClick={() => setEditMode(true)}>
                <IconEdit size={14} className="me-1" />
                Editar antes de aplicar
              </Button>
            )}
            <Button
              variant="success"
              onClick={handleApply}
              disabled={editMode ? !editedValue.trim() : !improvedValue.trim()}
            >
              <IconCheck size={14} className="me-1" />
              {editMode ? 'Aplicar minha versão' : 'Aplicar sugestão'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * AnalyzeIntentModal — wrapper para análise de rotas do intent_router.
 *
 * Props:
 *  - intentLabel: string — label da rota sendo analisada
 *  - (demais props repassadas para AnalyzeModal)
 */
const AnalyzeIntentModal = ({ intentLabel, ...rest }) => (
  <AnalyzeModal
    {...rest}
    title={<>Análise da rota: <code>{intentLabel}</code></>}
    currentValueLabel="Atual"
    improvedValueLabel="Sugerida"
    improvedField="improved_description"
    showOverlapWarnings
    showExamples
  />
);

export default AnalyzeIntentModal;
